import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormGroup, FormControlLabel, Tabs, Tab, Avatar, IconButton, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { atlApi } from '../services/atlApi';

const ATLManageData = () => {
  const [tab, setTab] = useState('data');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pivot, setPivot] = useState({ year: new Date().getFullYear(), rows: [] });
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [advertiser, setAdvertiser] = useState('');
  const [batchId, setBatchId] = useState('');
  const [channel, setChannel] = useState('');
  const [subBrand, setSubBrand] = useState('');
  const [beerCat, setBeerCat] = useState('');
  const [region, setRegion] = useState('NAT');
  const [advertiserOptions, setAdvertiserOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [subBrandOptions, setSubBrandOptions] = useState([]);
  const [beerCatOptions, setBeerCatOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [batches, setBatches] = useState([]);
  // AI panel state
  const [aiPrompt, setAiPrompt] = useState('brand vs channel 2025');
  // Removed chart type control; default to 'auto'
  const [aiLoading, setAiLoading] = useState(false);
  // Removed Gemini toggle; always use Gemini mode
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant'|'separator', payload}
  const [sessionStartIndex, setSessionStartIndex] = useState(0);
  const chatEndRef = useRef(null);
  const sendingRef = useRef(false);
  const newSession = () => {
    // Start a new logical session: keep previous messages in view, but reset context slice
    setSessionStartIndex(messages.length + 1);
    setMessages((prev) => [...prev, { role: 'separator', payload: { text: 'New Session' } }]);
    setAiPrompt('');
  };

  // @brand suggestions state
  const [showBrandSuggest, setShowBrandSuggest] = useState(false);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [brandSuggestIndex, setBrandSuggestIndex] = useState(-1);
  const brandDebounceRef = useRef(null);
  const promptContainerRef = useRef(null);

  const fetchBrandSuggestions = async (q) => {
    try {
      const res = await atlApi.autocompleteBrands(q || '', 20);
      setBrandSuggestions(Array.isArray(res?.items) ? res.items : []);
      setBrandSuggestIndex(res?.items && res.items.length ? 0 : -1);
    } catch (e) {
      setBrandSuggestions([]);
      setBrandSuggestIndex(-1);
    }
  };

  const handlePromptChange = (value) => {
    setAiPrompt(value);
    // Detect last @token
    const caretStr = value; // simple approach without caret pos
    const lastAt = caretStr.lastIndexOf('@');
    if (lastAt === -1) {
      setShowBrandSuggest(false);
      return;
    }
    // token is from @ until next whitespace or end
    const after = caretStr.slice(lastAt + 1);
    const spaceIdx = after.search(/\s/);
    const token = spaceIdx === -1 ? after : after.slice(0, spaceIdx);
    // Only show if @ is at start or preceded by whitespace
    const beforeChar = lastAt > 0 ? caretStr[lastAt - 1] : ' ';
    const validBoundary = /\s|[([,]/.test(beforeChar);
    if (!validBoundary) {
      setShowBrandSuggest(false);
      return;
    }
    setShowBrandSuggest(true);
    // debounce API
    if (brandDebounceRef.current) clearTimeout(brandDebounceRef.current);
    brandDebounceRef.current = setTimeout(() => {
      fetchBrandSuggestions(token);
    }, 200);
  };

  const replaceAtToken = (choice) => {
    const value = aiPrompt;
    const lastAt = value.lastIndexOf('@');
    if (lastAt === -1) return;
    const after = value.slice(lastAt + 1);
    const spaceIdx = after.search(/\s/);
    const tokenLen = spaceIdx === -1 ? after.length : spaceIdx;
    const before = value.slice(0, lastAt);
    const tail = value.slice(lastAt + 1 + tokenLen);
    const inserted = `@${choice?.canonical || choice?.display || ''}`;
    const newValue = `${before}${inserted}${tail}`;
    setAiPrompt(newValue);
    setShowBrandSuggest(false);
  };

  const onPromptKeyDown = (e) => {
    if (!showBrandSuggest || !brandSuggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setBrandSuggestIndex((idx) => (idx + 1) % brandSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setBrandSuggestIndex((idx) => (idx - 1 + brandSuggestions.length) % brandSuggestions.length);
    } else if (e.key === 'Enter') {
      if (brandSuggestIndex >= 0 && brandSuggestIndex < brandSuggestions.length) {
        e.preventDefault();
        replaceAtToken(brandSuggestions[brandSuggestIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowBrandSuggest(false);
    }
  };

  // Pivot Table Component
  const AIPivotTable = ({ data, title = "Pivot Table" }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 200, 
          color: 'text.secondary',
          fontStyle: 'italic'
        }}>
          No pivot data available
        </Box>
      );
    }

    const columns = Object.keys(data[0] || {});
    const numericColumns = columns.filter(col => 
      data.some(row => typeof row[col] === 'number' || !isNaN(parseFloat(row[col])))
    );
    // const textColumns = columns.filter(col => !numericColumns.includes(col));

    return (
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          {title}
        </Typography>
        
        {/* Summary Stats */}
        {numericColumns.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Summary Statistics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {numericColumns.map(col => {
                const values = data.map(row => parseFloat(row[col]) || 0).filter(v => !isNaN(v));
                const sum = values.reduce((a, b) => a + b, 0);
                const avg = values.length > 0 ? sum / values.length : 0;
                const max = Math.max(...values, 0);
                // Removed unused min calculation
                
                return (
                  <Box key={col} sx={{ minWidth: 120 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      {col}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, fontSize: '0.75rem' }}>
                      <span>Sum: {sum.toLocaleString()}</span>
                      <span>Avg: {avg.toFixed(0)}</span>
                      <span>Max: {max.toLocaleString()}</span>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Pivot Table */}
        <Box sx={{ 
          width: '100%', 
          overflowX: 'auto', 
          borderRadius: 1, 
          border: '1px solid', 
          borderColor: 'divider',
          maxHeight: 400
        }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ bgcolor: 'primary.main' }}>
              <TableRow>
                {columns.map((col) => (
                  <TableCell 
                    key={col} 
                    sx={{ 
                      whiteSpace: 'nowrap', 
                      fontWeight: 600, 
                      color: 'primary.contrastText',
                      minWidth: 120
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow 
                  key={idx} 
                  sx={{ 
                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  {columns.map((col) => (
                    <TableCell 
                      key={col} 
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontWeight: numericColumns.includes(col) ? 500 : 'normal'
                      }}
                    >
                      {numericColumns.includes(col) 
                        ? (parseFloat(row[col]) || 0).toLocaleString()
                        : String(row[col] || '')
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        
        <Box sx={{ mt: 1, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
          Showing {data.length} rows
        </Box>
      </Box>
    );
  };

  // Enhanced SVG chart renderer for agent UI
  const AIChart = ({ ui }) => {
    // Hooks must be top-level
    const [selected, setSelected] = React.useState(new Set());
    const [hover, setHover] = React.useState(null); // {x,label,value}
    const [containerSize, setContainerSize] = React.useState({ width: 800, height: 400 });
    const containerRef = React.useRef(null);
    
    React.useEffect(() => {
      const keys = (ui && Array.isArray(ui.seriesKeys)) ? ui.seriesKeys : [];
      setSelected(new Set(keys));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ui && ui.seriesKeys ? ui.seriesKeys.join('|') : '']);
    
    // Responsive sizing
    React.useEffect(() => {
      const updateSize = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerSize({
            width: Math.max(600, rect.width - 32),
            height: Math.max(300, Math.min(500, rect.width * 0.4))
          });
        }
      };
      
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, []);
    
    if (!ui || !ui.template || !ui.data || !Array.isArray(ui.data)) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 200, 
          color: 'text.secondary',
          fontStyle: 'italic'
        }}>
          No chart data available
        </Box>
      );
    }
    
    const template = ui.template;
    // Responsive dimensions
    const width = containerSize.width;
    const height = containerSize.height;
    const padding = { 
      top: 20, 
      right: Math.min(160, width * 0.15), 
      bottom: 80, // Increased for rotated labels
      left: Math.max(60, width * 0.08) 
    };
    const palette = ['#2563eb','#7c3aed','#ea580c','#16a34a','#6d28d9','#0891b2','#db2777','#0ea5e9','#dc2626','#0f766e'];

    // Helpers
    const getDomain = (vals) => {
      const nums = vals.map(v => Number(v) || 0);
      const max = Math.max(1, ...nums);
      return { min: 0, max };
    };
    const unique = (arr) => Array.from(new Set(arr));
    const fmtY = (v, isPercent=false) => {
      const n = Number(v) || 0;
      if (isPercent) return `${(n).toFixed(0)}%`;
      if (n >= 1e9) return (n/1e9).toFixed(1)+'B';
      if (n >= 1e6) return (n/1e6).toFixed(1)+'M';
      if (n >= 1e3) return (n/1e3).toFixed(1)+'K';
      return n.toLocaleString();
    };
    const parseTimeOrder = (xs) => {
      // Try parse YYYY-MM first; fallback lexicographic
      const isYm = /^\d{4}-\d{2}$/;
      const arr = xs.map(String);
      if (arr.every(s => isYm.test(s))) {
        return arr.sort((a,b)=> a.localeCompare(b));
      }
      return arr;
    };

    // Line multi-series: x categorical, multiple seriesKeys
    if (template === 'line_multi' && ui.xKey && ui.seriesKeys && Array.isArray(ui.seriesKeys)) {
      const xKey = ui.xKey;
      // Sort legend by total desc and cap to top-12 for readability
      const totalsMap = {};
      (ui.data || []).forEach(r => ui.seriesKeys.forEach(sk => { totalsMap[sk] = (totalsMap[sk]||0) + (Number(r[sk])||0); }));
      const seriesKeys = ui.seriesKeys.slice().sort((a,b)=> (totalsMap[b]||0) - (totalsMap[a]||0)).slice(0,12);
      const rows = ui.data;
      const xVals = parseTimeOrder(unique(rows.map(r => String(r[xKey]))));
      const toggle = (k) => {
        const next = new Set(selected);
        next.has(k) ? next.delete(k) : next.add(k);
        setSelected(next);
      };

      const allY = [];
      rows.forEach(r => seriesKeys.forEach(sk => allY.push(r[sk] || 0)));
      const yDom = getDomain(allY);
      const isPercent = xKey === 'month' && (Object.keys(rows[0]||{}).some(k=>/sos/i.test(k)) || (Math.max(...allY) <= 100 && Math.min(...allY) >= 0));

      const xScale = (v) => {
        const i = xVals.indexOf(String(v));
        const innerW = width - padding.left - padding.right;
        return padding.left + (i * (innerW / Math.max(1, xVals.length - 1)));
      };
      const yScale = (v) => {
        const innerH = height - padding.top - padding.bottom;
        const t = (Number(v) || 0) / (yDom.max || 1);
        return padding.top + (innerH * (1 - t));
      };
      const colors = palette;

      return (
        <Box ref={containerRef} sx={{ width: '100%', overflow: 'hidden' }}>
          {/* Legend Controls */}
          <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              Data Series (click to toggle)
            </Typography>
            <FormGroup row sx={{ flexWrap: 'wrap', gap: 1 }}>
              {seriesKeys.map((k, idx) => (
                <FormControlLabel 
                  key={k} 
                  control={
                    <Checkbox 
                      size="small" 
                      checked={selected.has(k)} 
                      onChange={()=>toggle(k)}
                      sx={{ 
                        '&.Mui-checked': { color: colors[idx % colors.length] }
                      }}
                    />
                  } 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          bgcolor: colors[idx % colors.length],
                          borderRadius: 0.5,
                          opacity: selected.has(k) ? 1 : 0.3,
                          boxShadow: `0 0 0 2px rgba(0,0,0,0.04)`
                        }} 
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {k}
                      </Typography>
                    </Box>
                  } 
                  sx={{ 
                    mr: 0,
                    '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                  }}
                />
              ))}
            </FormGroup>
          </Box>
          
          {/* Chart Container */}
          <Box sx={{ 
            width: '100%', 
            height: height, 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper'
          }}>
            <svg width={width} height={height} style={{ maxWidth: '100%', height: '100%' }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
                const yv = Math.round(yDom.max * t);
                const y = yScale(yv);
                return (
                  <g key={i}>
                    <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#f3f4f6" strokeWidth={1} />
                  </g>
                );
              })}
              
              {/* Y axis */}
              <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#94a3b8" strokeWidth={1.5} />
              {/* X axis */}
              <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#94a3b8" strokeWidth={1.5} />
              
              {/* X ticks with smart label positioning */}
              {xVals.map((v, i) => {
                const x = xScale(v);
                const label = String(v);
                const labelWidth = label.length * 6; // Approximate character width
                const spacing = (width - padding.left - padding.right) / Math.max(1, xVals.length);
                const shouldRotate = spacing < labelWidth + 10; // Rotate if not enough space
                
                return (
                  <g key={v}>
                    <line x1={x} y1={height - padding.bottom} x2={x} y2={height - padding.bottom + 6} stroke="#666" strokeWidth={1} />
                    <text 
                      x={x} 
                      y={height - padding.bottom + (shouldRotate ? 35 : 20)} 
                      fontSize={shouldRotate ? 10 : 11} 
                      textAnchor="middle" 
                      fill="#374151"
                      transform={shouldRotate ? `rotate(-45, ${x}, ${height - padding.bottom + 35})` : ''}
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
              
              {/* Y ticks */}
              {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
                const yv = Math.round(yDom.max * t);
                const y = yScale(yv);
                return (
                  <g key={i}>
                    <line x1={padding.left - 6} y1={y} x2={padding.left} y2={y} stroke="#666" strokeWidth={1} />
                    <text x={padding.left - 10} y={y + 4} fontSize={11} textAnchor="end" fill="#374151">
                      {fmtY(yv, isPercent)}
                    </text>
                  </g>
                );
              })}
              
              {/* Series */}
              {seriesKeys.map((k, idx) => {
                if (!selected.has(k)) return null;
                const path = [];
                rows.forEach((r, i) => {
                  const x = xScale(r[xKey]);
                  const y = yScale(r[k] || 0);
                  path.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
                });
                return (
                  <g key={k}>
                    <path 
                      d={path.join(' ')} 
                      fill="none" 
                      stroke={colors[idx % colors.length]} 
                      strokeWidth={3.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ filter: 'drop-shadow(0px 1px 0.75px rgba(0,0,0,0.15))' }}
                    />
                    {rows.map((r, i) => {
                      const xv = xScale(r[xKey]);
                      const yv = yScale(r[k] || 0);
                      return (
                        <circle 
                          key={i} 
                          cx={xv} 
                          cy={yv} 
                          r={4.5} 
                          fill={colors[idx % colors.length]}
                          stroke="#fff"
                          strokeWidth={2.5}
                          onMouseEnter={()=>setHover({x:xv,y:yv,label:k,value:r[k],tick:r[xKey]})} 
                          onMouseLeave={()=>setHover(null)}
                          style={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </g>
                );
              })}
              
              {/* Tooltip */}
              {hover && (
                <g transform={`translate(${Math.min(hover.x + 10, width - 220)}, ${Math.max(hover.y - 10, 10)})`}>
                  <rect x={0} y={-36} width={210} height={32} rx={8} ry={8} fill="#111827" opacity={0.95} />
                  <text x={10} y={-16} fontSize={12} fill="#e5e7eb" fontWeight="600">
                    {hover.tick}
                  </text>
                  <text x={10} y={-4} fontSize={12} fill="#f9fafb" fontWeight="500">
                    {hover.label}: {fmtY(hover.value, isPercent)}
                  </text>
                </g>
              )}
            </svg>
          </Box>
        </Box>
      );
    }

    // Bar grouped (single series per x)
    if (template === 'bar_grouped' && ui.xKey && ui.seriesKeys && ui.seriesKeys.length === 1) {
      const xKey = ui.xKey; const yKey = ui.seriesKeys[0]; const rows = ui.data;
      const xVals = rows.map(r => String(r[xKey]));
      const yDom = getDomain(rows.map(r => r[yKey] || 0));
      const innerW = width - padding.left - padding.right;
      const barW = Math.max(8, innerW / Math.max(1, xVals.length * 1.2));
      const xPos = (i) => padding.left + i * (innerW / Math.max(1, xVals.length)) - barW / 2;
      const yScale = (v) => {
        const innerH = height - padding.top - padding.bottom;
        const t = (Number(v) || 0) / (yDom.max || 1);
        return padding.top + (innerH * (1 - t));
      };
      
      // Update padding for bar chart to accommodate rotated labels
      const barPadding = { 
        top: 20, 
        right: Math.min(160, width * 0.15), 
        bottom: 80, // Increased for rotated labels
        left: Math.max(60, width * 0.08) 
      };
      
      return (
        <Box ref={containerRef} sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ 
            width: '100%', 
            height: height, 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper'
          }}>
            <svg width={width} height={height} style={{ maxWidth: '100%', height: '100%' }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
                const yv = Math.round(yDom.max * t);
                const y = yScale(yv);
                return (
                  <g key={i}>
                    <line x1={barPadding.left} y1={y} x2={width - barPadding.right} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                  </g>
                );
              })}
              
              {/* Y axis */}
              <line x1={barPadding.left} y1={barPadding.top} x2={barPadding.left} y2={height - barPadding.bottom} stroke="#666" strokeWidth={2} />
              {/* X axis */}
              <line x1={barPadding.left} y1={height - barPadding.bottom} x2={width - barPadding.right} y2={height - barPadding.bottom} stroke="#666" strokeWidth={2} />
              
              {/* Bars */}
              {rows.map((r, i) => (
                <g key={i}>
                  <rect 
                    x={xPos(i)} 
                    y={yScale(r[yKey] || 0)} 
                    width={barW} 
                    height={Math.max(0, (height - barPadding.bottom) - yScale(r[yKey] || 0))} 
                    fill="#1976d2"
                    stroke="#fff"
                    strokeWidth={1}
                    rx={2}
                    ry={2}
                    onMouseEnter={()=>setHover({x:xPos(i) + barW/2, y:yScale(r[yKey] || 0), label:yKey, value:r[yKey], tick:r[xKey]})} 
                    onMouseLeave={()=>setHover(null)}
                    style={{ cursor: 'pointer' }}
                  />
                  <text 
                    x={xPos(i) + barW / 2} 
                    y={height - barPadding.bottom + 20} 
                    fontSize={10} 
                    textAnchor="middle" 
                    fill="#333"
                    transform={`rotate(-45, ${xPos(i) + barW / 2}, ${height - barPadding.bottom + 20})`}
                  >
                    {String(r[xKey])}
                  </text>
                </g>
              ))}
              
              {/* Y ticks */}
              {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
                const yv = Math.round(yDom.max * t);
                const y = yScale(yv);
                return (
                  <g key={i}>
                    <line x1={barPadding.left - 6} y1={y} x2={barPadding.left} y2={y} stroke="#666" strokeWidth={1} />
                    <text x={barPadding.left - 10} y={y + 4} fontSize={11} textAnchor="end" fill="#333">
                      {fmtY(yv, false)}
                    </text>
                  </g>
                );
              })}
              
              {/* Tooltip */}
              {hover && (
                <g transform={`translate(${Math.min(hover.x + 10, width - 180)}, ${Math.max(hover.y - 10, 10)})`}>
                  <rect x={0} y={-28} width={170} height={26} rx={6} ry={6} fill="#212121" opacity={0.9} />
                  <text x={8} y={-10} fontSize={12} fill="#fff" fontWeight="500">
                    {hover.tick} • {hover.label}: {fmtY(hover.value, false)}
                  </text>
                </g>
              )}
            </svg>
          </Box>
        </Box>
      );
    }

    // Fallback: show JSON or error
    return (
      <Box ref={containerRef} sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ 
          width: '100%', 
          height: 300, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          p: 2
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'warning.main' }}>
            Chart Data (Raw)
          </Typography>
          <Box sx={{ 
            height: 'calc(100% - 40px)', 
            overflow: 'auto',
            bgcolor: 'grey.50',
            p: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <pre style={{ 
              margin: 0, 
              whiteSpace: 'pre-wrap', 
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}>
              {JSON.stringify(ui, null, 2)}
            </pre>
          </Box>
        </Box>
      </Box>
    );
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const handleImport = async () => {
    if (!file) return;
    try {
      setLoading(true);
      const data = await atlApi.importCompetitors(file);
      setResult(data);
      window.showToast?.({ type: data.status === 'success' ? 'success' : 'error', title: 'Import completed' });
    } catch (err) {
      setResult({ status: 'error', errors: [err?.response?.data || err.message] });
      window.showToast?.({ type: 'error', title: 'Import failed' });
    } finally {
      setLoading(false);
    }
  };

  const loadPivot = async (y, adv, bid, reg = 'NAT') => {
    try {
      setLoading(true);
      const data = await atlApi.getTable(y, adv, bid, reg);
      setPivot(data);
      // Derive filter options from rows
      const rows = data?.rows || [];
      const advSet = new Set();
      const chSet = new Set();
      const sbSet = new Set();
      const bcSet = new Set();
      const regSet = new Set();
      for (const r of rows) {
        if (r?.['Advertisers']) advSet.add(r['Advertisers']);
        if (r?.['Channel']) chSet.add(r['Channel']);
        if (r?.['Sub brand']) sbSet.add(r['Sub brand']);
        if (r?.['Beer Cat']) bcSet.add(r['Beer Cat']);
        if (r?.['Region']) regSet.add(r['Region']);
      }
      setAdvertiserOptions(Array.from(advSet).sort());
      setChannelOptions(Array.from(chSet).sort());
      setSubBrandOptions(Array.from(sbSet).sort());
      setBeerCatOptions(Array.from(bcSet).sort());
      // Only load region options if not in NAT mode
      if (reg !== 'NAT') {
        setRegionOptions(Array.from(regSet).sort());
      }
    } catch (err) {
      window.showToast?.({ type: 'error', title: 'Load pivot failed' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial load
    loadPivot(year, advertiser, '', region);
    // load batches list
    (async () => {
      try {
        const res = await atlApi.listBatches();
        setBatches(res?.batches || []);
      } catch (e) {
        // ignore silently; optional feature
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load region options when needed
  useEffect(() => {
    const loadRegionOptions = async () => {
      try {
        const res = await atlApi.autocompleteRegions('', 100);
        setRegionOptions(res?.items?.map(item => item.display) || []);
      } catch (e) {
        // ignore silently; optional feature
      }
    };
    loadRegionOptions();
  }, []);

  const handleReload = () => {
    loadPivot(year, advertiser, batchId, region);
  };

  const runAI = async () => {
    if (!aiPrompt || aiPrompt.trim() === '') return;
    if (sendingRef.current) return;
    try {
      sendingRef.current = true;
      setAiLoading(true);
      // append user message
      const userMsg = { role: 'user', payload: { text: aiPrompt }, ts: Date.now() };
      const nextMessages = [...messages, userMsg];
      setMessages(nextMessages);
      // Build lightweight history array from current session only
      const sessionMsgs = (nextMessages || []).slice(sessionStartIndex);
      const recent = sessionMsgs.slice(-8); // cap to last 8 within session
      const history = recent.map(m => {
        if (m.role === 'separator') return '---';
        if (m.role === 'user') return m.payload?.text || '';
        // assistant: prefer plain text if available, else summary or analysis
        const p = m.payload || {};
        return p.text || p.summary || p.conclusion || p.analysis || JSON.stringify(p).slice(0, 1000);
      });
      const res = await atlApi.runAgent(aiPrompt, 'auto', true, history);
      const asstMsg = { role: 'assistant', payload: res, ts: Date.now() };
      setMessages((prev) => [...prev, asstMsg]);
      setAiPrompt('');
      if (res?.chart) {
        // Optional: show toast with saved chart filename
        window.showToast?.({ type: 'success', title: `Chart generated: ${res.chart}` });
      }
    } catch (e) {
      window.showToast?.({ type: 'error', title: 'AI run failed' });
    } finally {
      setAiLoading(false);
      sendingRef.current = false;
    }
  };
  const formatTime = (ts) => {
    try {
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (_) { return ''; }
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, aiLoading]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>ATL Manage Data</Typography>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v)=>setTab(v)} aria-label="ATL tabs" variant="scrollable" scrollButtons="auto">
          <Tab label="Data" value="data" />
          <Tab label="Agent" value="agent" />
        </Tabs>
      </Paper>
      {tab === 'data' && (
      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}> 
          Choose File
          <input type="file" hidden onChange={handleFileChange} accept=".csv,.xlsx,.xls" />
        </Button>
        <Typography variant="body2" sx={{ flex: 1 }}>{file ? file.name : 'No file selected'}</Typography>
        <Button variant="contained" onClick={handleImport} disabled={!file || loading}>
          {loading ? 'Importing...' : 'Import'}
        </Button>
      </Paper>
      )}

      {tab === 'data' && result && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1">Result</Typography>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(result, null, 2)}</pre>
        </Paper>
      )}

      {tab === 'data' && (
      <Paper sx={{ p: 2, mt: 2, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Pivot {pivot.year}</Typography>
          <TextField size="small" label="Year" value={year} onChange={(e)=>setYear(e.target.value)} sx={{ width: 120 }} disabled={Boolean(batchId)} />
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="atl-batch-label">Version (Batch)</InputLabel>
            <Select
              labelId="atl-batch-label"
              label="Version (Batch)"
              value={batchId}
              onChange={(e)=>setBatchId(e.target.value)}
            >
              <MenuItem value=""><em>Latest (by Year)</em></MenuItem>
              {batches.map(b => (
                <MenuItem key={b.batch_id} value={b.batch_id}>
                  {b.batch_id.slice(0,8)} • {b.created_at_min?.slice(0,10) || ''} → {b.created_at_max?.slice(0,10) || ''} • {b.record_count} rows
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="atl-adv-label">Advertiser</InputLabel>
            <Select
              labelId="atl-adv-label"
              label="Advertiser"
              value={advertiser}
              onChange={(e)=>setAdvertiser(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {advertiserOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="atl-channel-label">Channel</InputLabel>
            <Select
              labelId="atl-channel-label"
              label="Channel"
              value={channel}
              onChange={(e)=>setChannel(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {channelOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="atl-subbrand-label">Sub brand</InputLabel>
            <Select
              labelId="atl-subbrand-label"
              label="Sub brand"
              value={subBrand}
              onChange={(e)=>setSubBrand(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {subBrandOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="atl-beercat-label">Beer Cat</InputLabel>
            <Select
              labelId="atl-beercat-label"
              label="Beer Cat"
              value={beerCat}
              onChange={(e)=>setBeerCat(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {beerCatOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="atl-region-label">Region</InputLabel>
            <Select
              labelId="atl-region-label"
              label="Region"
              value={region}
              onChange={(e)=>{
                setRegion(e.target.value);
                // Auto-reload when region changes
                setTimeout(() => {
                  loadPivot(year, advertiser, batchId, e.target.value);
                }, 100);
              }}
            >
              <MenuItem value="NAT"><em>NAT (All Regions)</em></MenuItem>
              {regionOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleReload} disabled={loading}>Reload</Button>
        </Box>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1200, tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                {['Advertisers','Brand','Sub brand','Beer Cat','Channel focus','Channel','Region','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Total'].map(h => (
                  <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const headers = ['Advertisers','Brand','Sub brand','Beer Cat','Channel focus','Channel','Region','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Total'];
                const monthKeys = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const filtered = (pivot.rows || []).filter(r => {
                if (advertiser && r['Advertisers'] !== advertiser) return false;
                if (channel && r['Channel'] !== channel) return false;
                if (subBrand && r['Sub brand'] !== subBrand) return false;
                if (beerCat && r['Beer Cat'] !== beerCat) return false;
                // For NAT view, show all data (already aggregated by backend)
                // For specific region view, filter by region
                if (region && region !== 'NAT' && r['Region'] !== region) return false;
                return true;
                });

                const totals = { Total: 0 };
                for (const m of monthKeys) totals[m] = 0;
                filtered.forEach(r => {
                  for (const m of monthKeys) {
                    const v = Number(r[m] ?? 0);
                    totals[m] += isNaN(v) ? 0 : v;
                  }
                  const tv = Number(r['Total'] ?? 0);
                  totals['Total'] += isNaN(tv) ? 0 : tv;
                });

                return (
                  <>
                    {filtered.map((r, idx) => (
                      <TableRow key={idx}>
                        {headers.map(k => (
                          <TableCell key={k} sx={{ whiteSpace: 'nowrap' }}>{r[k]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      {monthKeys.map(k => (
                        <TableCell key={k} sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{Math.round((totals[k] + Number.EPSILON) * 100) / 100}</TableCell>
                      ))}
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{Math.round((totals['Total'] + Number.EPSILON) * 100) / 100}</TableCell>
                    </TableRow>
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </Box>
      </Paper>
      )}

      {tab === 'agent' && (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: 600 }}>
        {/* Header Section */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              ATL Data Agent
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={newSession}
              sx={{ minWidth: 100 }}
            >
              New Session
            </Button>
          </Box>
        </Paper>

        {/* Chat Messages Section */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
            {messages.length === 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ mb: 2, opacity: 0.7 }}>
                  Welcome to ATL Data Agent
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Ask questions about your ATL data, such as:
                </Typography>
                <Box sx={{ textAlign: 'left', maxWidth: 400 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>• "Show me @Heineken spending by channel"</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>• "Compare @Tiger vs @Sapporo in Q1 2025"</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>• "What's the trend for Digital advertising?"</Typography>
                  <Typography variant="body2">• "Generate a chart for beer category performance"</Typography>
                </Box>
              </Box>
            )}
            
            {messages.map((m, idx) => (
              m.role === 'separator' ? (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ mx: 2, color: 'text.secondary' }}>New Session</Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.6 }} />
                </Box>
              ) : (
                <Box key={idx} sx={{ display: 'flex', justifyContent: m.role==='user' ? 'flex-end' : 'flex-start', mb: 2, gap: 1.25 }}>
                {m.role !== 'user' && (
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>A</Avatar>
                )}
                <Paper sx={{ 
                  p: 2, 
                  maxWidth: '80%', 
                  borderRadius: 3,
                  bgcolor: m.role==='user' ? 'primary.main' : 'grey.50', 
                  color: m.role==='user' ? 'primary.contrastText' : 'text.primary',
                  boxShadow: 2
                }}>
                  {m.role === 'user' && (
                    <>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                        {m.payload?.text || ''}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                        {formatTime(m.ts)}
                      </Typography>
                    </>
                  )}
                  {m.role === 'assistant' && (
                    <Box>
                      {/* Text Summary/Explanation */}
                      {m.payload?.text && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Analysis Summary
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                              {m.payload.text}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
                        {formatTime(m.ts)}
                      </Typography>
                      
                      {/* Conclusion/Key Findings */}
                      {m.payload?.conclusion && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'success.main' }}>
                            Key Conclusions
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontWeight: 500 }}>
                              {m.payload.conclusion}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      
                      {m.payload?.sql && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Generated SQL Query
                          </Typography>
                          <Paper sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', position: 'relative' }}>
                            <IconButton size="small" onClick={() => { try { navigator.clipboard.writeText(m.payload.sql || ''); window.showToast?.({ type: 'success', title: 'SQL copied' }); } catch(_) {} }} sx={{ position: 'absolute', top: 6, right: 6 }}>
                              <Tooltip title="Copy SQL">
                                <ContentCopyIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                              {m.payload.sql}
                            </pre>
                          </Paper>
                        </Box>
                      )}
                      
                      {m.payload?.table?.type === 'rows' && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Data Table {m.payload.table.total_rows && `(${m.payload.table.total_rows.toLocaleString()} rows)`}
                          </Typography>
                          <Box sx={{ width: '100%', overflowX: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Table size="small" stickyHeader>
                              <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                  {(m.payload.table.columns || []).map((c) => (
                                    <TableCell key={c} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                      {c}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(m.payload.table.rows || []).map((r, ridx) => (
                                  <TableRow key={ridx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                    {(m.payload.table.columns || []).map((c) => (
                                      <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}>
                                        {typeof r?.[c] === 'number' ? r[c].toLocaleString() : String(r?.[c] ?? '')}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Multiple Pivot Tables */}
                      {m.payload?.pivot_tables && m.payload.pivot_tables.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                            Crosstab Analysis
                          </Typography>
                          {m.payload.pivot_tables.map((pivotTable, idx) => (
                            <Box key={idx} sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                {pivotTable.title}
                              </Typography>
                              <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                  <Table size="small" stickyHeader>
                                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'grey.100', zIndex: 1 }}>
                                          {pivotTable.rowKey}
                                        </TableCell>
                                        {pivotTable.colKeys.map((col) => (
                                          <TableCell key={col} sx={{ fontWeight: 600, textAlign: 'right' }}>
                                            {col}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {pivotTable.rows.map((row, rowIdx) => (
                                        <TableRow key={rowIdx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                          <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'inherit', zIndex: 1 }}>
                                            {row[pivotTable.rowKey]}
                                          </TableCell>
                                          {pivotTable.colKeys.map((col) => (
                                            <TableCell key={col} sx={{ textAlign: 'right' }}>
                                              {typeof row[col] === 'number' ? row[col].toLocaleString() : String(row[col] || '')}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Paper>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Legacy Pivot Table (for backward compatibility) */}
                      {m.payload?.pivot && !m.payload?.pivot_tables && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Pivot Analysis
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <AIPivotTable data={m.payload.pivot} title="Data Pivot" />
                          </Paper>
                        </Box>
                      )}
                      
                      {m.payload?.ui && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Interactive Visualization
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <AIChart ui={m.payload.ui} />
                          </Paper>
                        </Box>
                      )}
                      
                      {m.payload?.insights && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Key Insights
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                            <pre style={{ 
                              margin: 0, 
                              whiteSpace: 'pre-wrap', 
                              fontSize: '0.875rem',
                              fontFamily: 'inherit'
                            }}>
                              {JSON.stringify(m.payload.insights, null, 2)}
                            </pre>
                          </Paper>
                        </Box>
                      )}
                      
                      {/* General Summary - fallback for any text content */}
                      {!m.payload?.text && !m.payload?.conclusion && (m.payload?.summary || m.payload?.description || m.payload?.analysis) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Summary
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                              {m.payload.summary || m.payload.description || m.payload.analysis}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      
                      {/* Raw Response - for debugging or when no structured content */}
                      {!m.payload?.text && !m.payload?.conclusion && !m.payload?.summary && !m.payload?.description && !m.payload?.analysis && 
                       !m.payload?.sql && !m.payload?.table && !m.payload?.ui && !m.payload?.pivot && !m.payload?.insights && 
                       Object.keys(m.payload || {}).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'warning.main' }}>
                            Agent Response
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, border: '1px solid', borderColor: 'warning.main' }}>
                            <pre style={{ 
                              margin: 0, 
                              whiteSpace: 'pre-wrap', 
                              fontSize: '0.875rem',
                              fontFamily: 'inherit'
                            }}>
                              {JSON.stringify(m.payload, null, 2)}
                            </pre>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
                {m.role === 'user' && (
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>U</Avatar>
                )}
              </Box>
              )
            ))}
            
            {aiLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  border: '2px solid', 
                  borderColor: 'primary.main', 
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />
                <Typography variant="body2">AI is thinking...</Typography>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>
          {/* Bottom Chat Bar */}
          <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, mt: 1, pt: 1, backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', zIndex: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Ask about ATL data... Use @ to insert brand names"
                value={aiPrompt}
                onChange={(e)=>handlePromptChange(e.target.value)}
                onKeyDown={(e)=>{ onPromptKeyDown(e); if (e.key==='Enter' && !e.shiftKey){ e.preventDefault(); runAI(); } }}
                inputRef={promptContainerRef}
                multiline
                minRows={1}
                maxRows={6}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                  }
                }}
              />
              {showBrandSuggest && (
                <Paper 
                  elevation={8} 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 56, 
                    left: 0, 
                    right: 0,
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    zIndex: 10,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                  onMouseDown={(e)=> e.preventDefault()}
                >
                  <Box>
                    {brandSuggestions.length === 0 && (
                      <Box sx={{ px: 2, py: 1.5, color: 'text.secondary', fontStyle: 'italic' }}>
                        No brand suggestions found
                      </Box>
                    )}
                    {brandSuggestions.map((it, idx) => (
                      <Box 
                        key={(it.canonical||it.display||'')+idx}
                        onClick={()=>replaceAtToken(it)}
                        onMouseEnter={()=>setBrandSuggestIndex(idx)}
                        sx={{ 
                          px: 2, 
                          py: 1.5, 
                          cursor: 'pointer', 
                          bgcolor: idx===brandSuggestIndex ? 'action.hover' : 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {it.display || it.canonical}
                        </Typography>
                        {it.canonical && it.canonical !== it.display && (
                          <Typography variant="caption" color="text.secondary">
                            {it.canonical}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
              <Button 
                variant="contained" 
                onClick={runAI} 
                disabled={aiLoading || !aiPrompt.trim()}
                sx={{ 
                  position: 'absolute', 
                  right: 8, 
                  bottom: 8,
                  borderRadius: 2,
                  minWidth: 80
                }}
              >
                {aiLoading ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
      )}
    </Box>
  );
};

export default ATLManageData;


